import React, { useState } from 'react';
import { Box, Divider, Stack, Typography, TextField } from '@mui/material';
import { z } from 'zod';
import { AddCircleOutline, Close } from '@mui/icons-material';

import { Button } from '../../../components/core/Button/Button';
import { ComplianceStatus } from './ComplianceStatus';
import Switch from '../../../common/Switch';
import { TermLink } from '../../../store/modules/merchant/types';

type Props = {
  headerText: string;
  inputLabel: string;
  existingTermLink?: TermLink;
  onTermLinkSubmit: (termLink: TermLink | undefined) => void;
  hasAdminAccess?: boolean;
  readOnly?: boolean;
};

export const TermLinkBox = ({
  headerText,
  inputLabel,
  existingTermLink,
  onTermLinkSubmit,
  hasAdminAccess = false,
  readOnly = false,
}: Props) => {
  const [localUrl, setLocalUrl] = useState<string>(existingTermLink?.url ?? '');
  const [inputError, setError] = useState<string>();

  return (
    <Stack
      sx={{
        border: '1px solid',
        borderColor: 'border.main',
        padding: 2,
        borderRadius: '12px',
        mt: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          mb: 2,
        }}
      >
        {headerText}
      </Box>
      {existingTermLink ? (
        <Stack>
          <Stack direction="row" spacing={2}>
            <Stack spacing={1} sx={{ width: 'calc(100% - 307px)' }}>
              <Typography variant="small" color="text.secondary">
                {inputLabel}
              </Typography>
              <Typography fontWeight="semibold">{existingTermLink.url}</Typography>
              {!readOnly && (
                <Stack
                  direction="row"
                  sx={{
                    mt: 1,
                    '& button': {
                      p: 0.5,
                      '& p': { color: 'link.main' },
                      '& svg': { color: 'link.main', fontSize: '16px !important' },
                    },
                  }}
                >
                  <Button
                    variant="text"
                    theme="secondary"
                    onClick={() => {
                      setLocalUrl('');
                      onTermLinkSubmit(undefined);
                    }}
                    aria-label="remove"
                    text="Remove URL"
                    startIcon={<Close />}
                  />
                </Stack>
              )}
            </Stack>
            <ComplianceStatus status={existingTermLink.complianceStatus} />
            <Box sx={{ marginLeft: 'auto !important' }}>
              {existingTermLink.complianceStatus === 'APPROVED' && (
                <Box>
                  <Typography variant="small" color="text.secondary" sx={{ mb: 1 }}>
                    Display link on checkout
                  </Typography>
                  <Switch
                    isChecked={existingTermLink.enabled}
                    isDisabled={readOnly}
                    onToggle={() =>
                      onTermLinkSubmit({
                        ...existingTermLink,
                        enabled: !existingTermLink.enabled,
                      })
                    }
                    aria-label="Display link on checkout switch"
                  />
                </Box>
              )}
            </Box>
          </Stack>
          {hasAdminAccess && existingTermLink.complianceStatus === 'PENDING' && (
            <>
              <Divider sx={{ my: '16px' }} />
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'end', '& button': { width: '150px' } }}
              >
                <Button
                  theme="secondary"
                  onClick={() =>
                    onTermLinkSubmit({
                      ...existingTermLink,
                      complianceStatus: 'REJECTED',
                    })
                  }
                  text="Deny URL"
                />
                <Button
                  theme="primary"
                  onClick={() =>
                    onTermLinkSubmit({
                      ...existingTermLink,
                      complianceStatus: 'APPROVED',
                    })
                  }
                  text="Approve URL"
                />
              </Stack>
            </>
          )}
        </Stack>
      ) : (
        <Stack>
          <TextField
            label={inputLabel}
            value={localUrl}
            onChange={(event) => setLocalUrl(event.target.value)}
            error={!!inputError}
            helperText={inputError}
            sx={{
              mb: '8px',
              maxWidth: '521px',
            }}
            disabled={readOnly}
          />
          {!readOnly && (
            <Box sx={{ '& button': { px: 8 } }}>
              <Button
                theme="secondary"
                onClick={() => {
                  setError(undefined);
                  if (!z.string().url().safeParse(localUrl).success || !localUrl) {
                    setError('Must be a valid URL');
                  } else {
                    onTermLinkSubmit({
                      url: localUrl,
                      enabled: false,
                    } as TermLink);
                  }
                }}
                text="Submit"
                startIcon={<AddCircleOutline fontSize="small" sx={{ mr: 1 }} />}
              />
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};
